import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { useHotel } from "../../contexts/HotelContext";
import { useToast } from "../../contexts/ToastContext";
import {
  useAdminTenants,
  useDeleteTenant,
} from "../../hooks/useAdminTenantDeletion";
import { DeleteTenantModal } from "../../components/admin/tenant-deletion/DeleteTenantModal";
import { TenantDeletionSpinner } from "../../components/admin/tenant-deletion/TenantDeletionSpinner";
import {
  extractTenantDeletionErrorCode,
  mapTenantDeletionErrorCode,
} from "../../components/admin/tenant-deletion/error-code-mapper";
import type {
  AdminTenantListItem,
  ClientSourceFilter,
  MarketingFilter,
} from "../../services/tenantDeletionApi";
import { MARKETING_FILTER_LABELS } from "../../services/tenantDeletionApi";
import {
  AdminTenantsTable,
  type AdminTenantsSortBy,
} from "./components/AdminTenantsTable";
import { AdminTenantsPagination } from "./components/AdminTenantsPagination";

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

/**
 * Platform-level admin tenants list (AC-28).
 * - Backed by GET /admin/tenants (paginated)
 * - Delete action triggers DeleteTenantModal → POST /admin/tenant-deletions
 * - On success: toast with link to /admin/tenant-deletions/:archiveId, list auto-refreshes
 *
 * Sub-components:
 * - {@link AdminTenantsTable} — table + rows (preserves scope="col", aria-sort)
 * - {@link AdminTenantsPagination} — prev/next + page counter (with aria-label)
 */
const AdminTenants: React.FC = () => {
  const { user } = useHotel();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<AdminTenantsSortBy>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [clientSourceFilter, setClientSourceFilter] = useState<ClientSourceFilter | "">("");
  const [marketingFilter, setMarketingFilter] = useState<MarketingFilter | "">("");

  const [target, setTarget] = useState<AdminTenantListItem | null>(null);

  const query = useMemo(
    () => ({
      page,
      limit: Math.min(limit, MAX_LIMIT),
      search: search || undefined,
      sortBy,
      sortOrder,
      clientSource: clientSourceFilter || undefined,
      marketing: marketingFilter || undefined,
    }),
    [page, limit, search, sortBy, sortOrder, clientSourceFilter, marketingFilter]
  );

  const { data, isLoading, isError, error } = useAdminTenants(query);
  const deleteMutation = useDeleteTenant();

  if (user?.role !== "admin") {
    return (
      <Layout>
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <h3 className="text-amber-800 font-medium">Brak dostępu</h3>
          <p className="text-amber-700 text-sm mt-1">
            Ta strona jest dostępna tylko dla globalnych administratorów.
          </p>
        </div>
      </Layout>
    );
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const handleSort = (column: AdminTenantsSortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setSearchInput("");
    setPage(1);
  };

  const handleDeleteSubmit = ({
    reason,
    tenantNameConfirmation,
  }: {
    reason: string;
    tenantNameConfirmation: string;
  }) => {
    if (!target) return;
    deleteMutation.mutate(
      {
        tenantId: target.id,
        reason,
        tenantNameConfirmation,
      },
      {
        onSuccess: (resp) => {
          setTarget(null);
          addToast({
            message: `Tenant został zarchiwizowany. Archiwum: ${resp.archiveId}`,
            type: "success",
            action: {
              label: "Zobacz archiwum",
              onClick: () => {
                navigate(`/admin/tenant-deletions/${resp.archiveId}`);
              },
            },
          });
        },
        onError: (err) => {
          const code = extractTenantDeletionErrorCode(err);
          addToast({
            message: mapTenantDeletionErrorCode(code),
            type: "error",
          });
        },
      }
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Tenanci (platforma)
          </h1>
          <p className="text-neutral-600 mt-1">
            Lista wszystkich tenantów platformowych. Możesz zainicjować archiwizację.
          </p>
        </div>

        {/* Search + filters row */}
        <div className="flex flex-wrap gap-2 items-center">
          <form onSubmit={handleSearchSubmit} className="flex gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Szukaj po nazwie lub NIP…"
              data-testid="admin-tenants-search-input"
              className="flex-1 max-w-md rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              type="submit"
              data-testid="admin-tenants-search-submit"
              className="rounded-md bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
            >
              Szukaj
            </button>
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
              >
                Wyczyść
              </button>
            )}
          </form>

          {/* Client source filter */}
          <label htmlFor="admin-tenants-client-source-filter" className="sr-only">
            Źródło klienta
          </label>
          <select
            id="admin-tenants-client-source-filter"
            aria-label="Źródło klienta"
            value={clientSourceFilter}
            onChange={(e) => {
              setClientSourceFilter(e.target.value as ClientSourceFilter | "");
              setPage(1);
            }}
            data-testid="admin-tenants-client-source-filter"
            className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">— Wszystkie źródła —</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="none">Brak źródła</option>
          </select>

          {/* Marketing filter (AC-11) */}
          <div>
            <label htmlFor="admin-tenants-marketing-filter" className="sr-only">
              Marketing
            </label>
            <select
              id="admin-tenants-marketing-filter"
              aria-label="Marketing"
              aria-describedby="admin-tenants-marketing-filter-hint"
              value={marketingFilter}
              onChange={(e) => {
                setMarketingFilter(e.target.value as MarketingFilter | "");
                setPage(1);
              }}
              data-testid="admin-tenants-marketing-filter"
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— Wszystkie —</option>
              <option value="sendable">{MARKETING_FILTER_LABELS.sendable}</option>
              <option value="opted_out">{MARKETING_FILTER_LABELS.opted_out}</option>
              <option value="no_consent">{MARKETING_FILTER_LABELS.no_consent}</option>
            </select>
            <p
              id="admin-tenants-marketing-filter-hint"
              data-testid="admin-tenants-marketing-filter-hint"
              className="text-xs text-neutral-500 mt-0.5 max-w-xs"
            >
              Filtr zawęża organizacje — przed wysyłką sprawdź zgody per osoba w
              zakładce „Marketing i sprzedaż”.
            </p>
          </div>
        </div>

        {/* Search context banner — visible only when filter active */}
        {search && (
          <div
            data-testid="admin-tenants-search-context"
            className="text-sm text-neutral-700"
          >
            Wyniki dla: <span className="font-medium">&quot;{search}&quot;</span>
            <button
              type="button"
              onClick={handleClearSearch}
              className="ml-2 text-primary-600 hover:underline text-xs"
            >
              (wyczyść)
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center justify-center min-h-32">
            <TenantDeletionSpinner
              data-testid="admin-tenants-loading"
              size="md"
              variant="primary"
            />
          </div>
        )}

        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <h3 className="text-red-800 font-medium">Błąd ładowania tenantów</h3>
            <p className="text-red-600 text-sm mt-1">
              {error instanceof Error ? error.message : "Nieznany błąd"}
            </p>
          </div>
        )}

        {!isLoading && !isError && data && (
          <>
            <AdminTenantsTable
              items={data.items}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              onDelete={setTarget}
            />

            <AdminTenantsPagination
              total={data.total}
              page={data.page}
              totalPages={totalPages}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={(n) => {
                setLimit(n);
                setPage(1);
              }}
              testIdPrefix="admin-tenants"
            />
          </>
        )}
      </div>

      {target && (
        <DeleteTenantModal
          isOpen={!!target}
          onClose={() => setTarget(null)}
          onSubmit={handleDeleteSubmit}
          tenant={{ id: target.id, name: target.name }}
          isPending={deleteMutation.isPending}
        />
      )}
    </Layout>
  );
};

export default AdminTenants;
